/**
 * LocalizationSystem - i18n support for all UI text.
 * Covers tasks 10.8, 14.1–14.15: Spanish, French, German, Italian, Portuguese,
 * Japanese, Korean, Chinese, Russian, Arabic, Hindi, Dutch, Polish, Swedish, Turkish.
 */

type TranslationMap = Record<string, string>;

const TRANSLATIONS: Record<string, TranslationMap> = {
  en: {
    // Menu
    "menu.title": "⚽ AMN FOOTBALL",
    "menu.subtitle": "WASD MOVE • SHIFT SPRINT • SPACE SHOOT • E THROUGH • Q LOB • F TACKLE",
    "menu.practice": "PRACTICE SESSION",
    "menu.startMatch": "START MATCH",
    "menu.matchLevel": "MATCH LEVEL",
    "menu.easy": "EASY",
    "menu.medium": "MEDIUM",
    "menu.hard": "HARD",
    "menu.elite": "ELITE",
    "menu.easy.desc": "ROOKIE: 1 BOT - FREE ROAM & CHASE",
    "menu.medium.desc": "AMATEUR: 2 BOTS - ZONE DEFENSE",
    "menu.hard.desc": "PRO: 3 BOTS - COORDINATED ATTACK",
    "menu.elite.desc": "ELITE: 11 BOTS - FULL 4-3-3 FORMATION",
    "menu.crowd": "HIGH PERFORMANCE CROWD",
    // HUD
    "hud.stamina": "STAMINA",
    "hud.power": "POWER",
    "hud.possession": "POSSESSION",
    "hud.shots": "SHOTS ON TARGET",
    "hud.controls": "SPACE SHOOT  •  E THROUGH  •  Q LOB  •  F TACKLE",
    "hud.fatigue": "FATIGUED",
    // End Screen
    "end.win": "MATCH WON",
    "end.loss": "DEFEATED",
    "end.draw": "DRAW",
    "end.rematch": "REMATCH",
    "end.stats": "MATCH STATISTICS",
    "end.shots": "Shots on Target",
    "end.possession": "Possession",
    "end.goals": "Goals",
    // Game Events
    "event.goal": "⚽ GOAL!",
    "event.conceded": "⚽ CONCEDED",
    "event.fantastic": "FANTASTIC FINISH!",
    "event.opponent": "OPPONENT SCORED",
    "event.halftime": "HALF TIME",
    "event.fulltime": "FULL TIME",
    "event.extratime": "EXTRA TIME",
    "event.throwIn": "THROW IN",
    "event.corner": "CORNER KICK",
    "event.penalty": "PENALTY!",
    "event.offside": "OFFSIDE",
    // Settings
    "settings.title": "SETTINGS",
    "settings.audio": "AUDIO",
    "settings.master": "MASTER VOLUME",
    "settings.sfx": "SFX VOLUME",
    "settings.music": "MUSIC VOLUME",
    "settings.commentary": "COMMENTARY",
    "settings.graphics": "GRAPHICS",
    "settings.weather": "WEATHER",
    "settings.weather.none": "NONE",
    "settings.weather.rain": "RAIN",
    "settings.weather.snow": "SNOW",
    "settings.duration": "MATCH DURATION",
    "settings.colorblind": "COLOR BLIND MODE",
    "settings.language": "LANGUAGE",
    "settings.controls": "CONTROLS",
    "settings.keybindings": "KEY BINDINGS",
    "settings.save": "SAVE",
    "settings.reset": "RESET",
    // Credits
    "credits.title": "CREDITS",
    "credits.dev": "Development",
    "credits.close": "CLOSE",
    // Achievements
    "achievement.first_win": "First Victory",
    "achievement.hat_trick": "Hat Trick Hero",
    "achievement.clean_sheet": "Clean Sheet",
    "achievement.veteran": "Veteran Player",
    "achievement.legend": "Living Legend",
    "achievement.goal_machine": "Goal Machine",
    "achievement.unbeaten_run": "Unbeaten Run",
    "achievement.marathon": "Marathon Player",
  },

  es: {
    "menu.title": "⚽ AMN FÚTBOL",
    "menu.subtitle": "WASD MOVER • SHIFT CORRER • ESPACIO DISPARAR • E PASE • Q LOB • F ENTRADA",
    "menu.practice": "SESIÓN DE PRÁCTICA",
    "menu.startMatch": "INICIAR PARTIDO",
    "menu.matchLevel": "NIVEL",
    "menu.easy": "FÁCIL",
    "menu.medium": "MEDIO",
    "menu.hard": "DIFÍCIL",
    "menu.elite": "ÉLITE",
    "menu.easy.desc": "NOVATO: 1 BOT",
    "menu.medium.desc": "AMATEUR: 2 BOTS",
    "menu.hard.desc": "PRO: 3 BOTS",
    "menu.elite.desc": "ÉLITE: 11 BOTS",
    "menu.crowd": "MULTITUD DE ALTO RENDIMIENTO",
    "hud.stamina": "RESISTENCIA",
    "hud.power": "POTENCIA",
    "hud.controls": "ESPACIO DISPARAR  •  E PASE  •  Q LOB  •  F ENTRADA",
    "end.win": "PARTIDO GANADO",
    "end.loss": "DERROTADO",
    "end.draw": "EMPATE",
    "end.rematch": "REVANCHA",
    "event.goal": "⚽ ¡GOL!",
    "event.halftime": "MEDIO TIEMPO",
    "event.fulltime": "TIEMPO COMPLETO",
    "settings.title": "AJUSTES",
    "settings.save": "GUARDAR",
  },

  fr: {
    "menu.title": "⚽ AMN FOOTBALL",
    "menu.subtitle": "WASD BOUGER • MAJ SPRINT • ESPACE TIRER • E PASSE • Q LOB • F TACLE",
    "menu.practice": "ENTRAÎNEMENT",
    "menu.startMatch": "DÉMARRER LE MATCH",
    "menu.matchLevel": "NIVEAU",
    "menu.easy": "FACILE",
    "menu.medium": "MOYEN",
    "menu.hard": "DIFFICILE",
    "menu.elite": "ÉLITE",
    "menu.easy.desc": "DÉBUTANT: 1 BOT",
    "menu.medium.desc": "AMATEUR: 2 BOTS",
    "menu.hard.desc": "PRO: 3 BOTS",
    "menu.elite.desc": "ÉLITE: 11 BOTS",
    "menu.crowd": "FOULE HAUTE PERFORMANCE",
    "hud.stamina": "ENDURANCE",
    "hud.power": "PUISSANCE",
    "hud.controls": "ESPACE TIRER  •  E PASSE  •  Q LOB  •  F TACLE",
    "end.win": "MATCH GAGNÉ",
    "end.loss": "DÉFAITE",
    "end.draw": "MATCH NUL",
    "end.rematch": "REVANCHE",
    "event.goal": "⚽ BUT!",
    "event.halftime": "MI-TEMPS",
    "event.fulltime": "FIN DU MATCH",
    "settings.title": "PARAMÈTRES",
    "settings.save": "SAUVEGARDER",
  },

  de: {
    "menu.title": "⚽ AMN FUSSBALL",
    "menu.subtitle": "WASD BEWEGEN • SHIFT SPRINT • LEERTASTE SCHUSS • E PASS • Q LOB • F GRÄTSCHE",
    "menu.practice": "TRAININGSEINHEIT",
    "menu.startMatch": "SPIEL STARTEN",
    "menu.matchLevel": "SCHWIERIGKEITSGRAD",
    "menu.easy": "LEICHT",
    "menu.medium": "MITTEL",
    "menu.hard": "SCHWER",
    "menu.elite": "ELITE",
    "menu.easy.desc": "ANFÄNGER: 1 BOT",
    "menu.medium.desc": "AMATEUR: 2 BOTS",
    "menu.hard.desc": "PROFI: 3 BOTS",
    "menu.elite.desc": "ELITE: 11 BOTS",
    "hud.stamina": "AUSDAUER",
    "hud.power": "KRAFT",
    "end.win": "SPIEL GEWONNEN",
    "end.loss": "NIEDERLAGE",
    "end.draw": "UNENTSCHIEDEN",
    "end.rematch": "RÜCKSPIEL",
    "event.goal": "⚽ TOR!",
    "event.halftime": "HALBZEIT",
    "event.fulltime": "SPIELENDE",
    "settings.title": "EINSTELLUNGEN",
    "settings.save": "SPEICHERN",
  },

  it: {
    "menu.title": "⚽ AMN CALCIO",
    "menu.practice": "ALLENAMENTO",
    "menu.startMatch": "INIZIA PARTITA",
    "menu.easy": "FACILE",
    "menu.medium": "MEDIO",
    "menu.hard": "DIFFICILE",
    "menu.elite": "ELITE",
    "hud.stamina": "RESISTENZA",
    "end.win": "PARTITA VINTA",
    "end.loss": "SCONFITTA",
    "end.draw": "PAREGGIO",
    "end.rematch": "RIVINCITA",
    "event.goal": "⚽ GOAL!",
    "event.halftime": "INTERVALLO",
    "event.fulltime": "FINE PARTITA",
    "settings.title": "IMPOSTAZIONI",
    "settings.save": "SALVA",
  },

  pt: {
    "menu.title": "⚽ AMN FUTEBOL",
    "menu.practice": "TREINO",
    "menu.startMatch": "INICIAR JOGO",
    "menu.easy": "FÁCIL",
    "menu.medium": "MÉDIO",
    "menu.hard": "DIFÍCIL",
    "menu.elite": "ELITE",
    "hud.stamina": "RESISTÊNCIA",
    "end.win": "JOGO GANHO",
    "end.loss": "DERROTA",
    "end.draw": "EMPATE",
    "end.rematch": "REVANCHE",
    "event.goal": "⚽ GOL!",
    "event.halftime": "INTERVALO",
    "event.fulltime": "FIM DE JOGO",
    "settings.title": "CONFIGURAÇÕES",
    "settings.save": "SALVAR",
  },

  ja: {
    "menu.title": "⚽ AMN フットボール",
    "menu.practice": "練習モード",
    "menu.startMatch": "試合開始",
    "menu.easy": "かんたん",
    "menu.medium": "ふつう",
    "menu.hard": "むずかしい",
    "menu.elite": "エリート",
    "hud.stamina": "スタミナ",
    "end.win": "勝利！",
    "end.loss": "敗北",
    "end.draw": "引き分け",
    "end.rematch": "再試合",
    "event.goal": "⚽ ゴール！",
    "event.halftime": "ハーフタイム",
    "event.fulltime": "試合終了",
    "settings.title": "設定",
    "settings.save": "保存",
  },

  ko: {
    "menu.title": "⚽ AMN 풋볼",
    "menu.practice": "연습 모드",
    "menu.startMatch": "경기 시작",
    "menu.easy": "쉬움",
    "menu.medium": "보통",
    "menu.hard": "어려움",
    "menu.elite": "엘리트",
    "hud.stamina": "스태미나",
    "end.win": "승리!",
    "end.loss": "패배",
    "end.draw": "무승부",
    "end.rematch": "재경기",
    "event.goal": "⚽ 골!",
    "event.halftime": "하프타임",
    "event.fulltime": "경기 종료",
    "settings.title": "설정",
    "settings.save": "저장",
  },

  zh: {
    "menu.title": "⚽ AMN 足球",
    "menu.practice": "练习模式",
    "menu.startMatch": "开始比赛",
    "menu.easy": "简单",
    "menu.medium": "普通",
    "menu.hard": "困难",
    "menu.elite": "精英",
    "hud.stamina": "体力",
    "end.win": "获胜！",
    "end.loss": "失败",
    "end.draw": "平局",
    "end.rematch": "再战",
    "event.goal": "⚽ 进球！",
    "event.halftime": "中场休息",
    "event.fulltime": "比赛结束",
    "settings.title": "设置",
    "settings.save": "保存",
  },

  ru: {
    "menu.title": "⚽ AMN ФУТБОЛ",
    "menu.practice": "ТРЕНИРОВКА",
    "menu.startMatch": "НАЧАТЬ МАТЧ",
    "menu.easy": "ЛЁГКИЙ",
    "menu.medium": "СРЕДНИЙ",
    "menu.hard": "СЛОЖНЫЙ",
    "menu.elite": "ЭЛИТА",
    "hud.stamina": "ВЫНОСЛИВОСТЬ",
    "end.win": "ПОБЕДА!",
    "end.loss": "ПОРАЖЕНИЕ",
    "end.draw": "НИЧЬЯ",
    "end.rematch": "РЕВАНШ",
    "event.goal": "⚽ ГОЛ!",
    "event.halftime": "ПЕРЕРЫВ",
    "event.fulltime": "КОНЕЦ МАТЧА",
    "settings.title": "НАСТРОЙКИ",
    "settings.save": "СОХРАНИТЬ",
  },

  ar: {
    "menu.title": "⚽ AMN كرة القدم",
    "menu.practice": "التدريب",
    "menu.startMatch": "بدء المباراة",
    "menu.easy": "سهل",
    "menu.medium": "متوسط",
    "menu.hard": "صعب",
    "menu.elite": "نخبة",
    "hud.stamina": "الطاقة",
    "end.win": "فوز!",
    "end.loss": "خسارة",
    "end.draw": "تعادل",
    "end.rematch": "مباراة ثأر",
    "event.goal": "⚽ هدف!",
    "event.halftime": "نصف الوقت",
    "event.fulltime": "نهاية المباراة",
    "settings.title": "الإعدادات",
    "settings.save": "حفظ",
  },

  hi: {
    "menu.title": "⚽ AMN फुटबॉल",
    "menu.practice": "अभ्यास",
    "menu.startMatch": "मैच शुरू करें",
    "menu.easy": "आसान",
    "menu.medium": "मध्यम",
    "menu.hard": "कठिन",
    "menu.elite": "एलीट",
    "hud.stamina": "ऊर्जा",
    "end.win": "जीत!",
    "end.loss": "हार",
    "end.draw": "ड्रॉ",
    "end.rematch": "रीमैच",
    "event.goal": "⚽ गोल!",
    "event.halftime": "हाफ टाइम",
    "event.fulltime": "पूरा समय",
    "settings.title": "सेटिंग्स",
    "settings.save": "सहेजें",
  },

  nl: {
    "menu.title": "⚽ AMN VOETBAL",
    "menu.practice": "OEFENSESSIE",
    "menu.startMatch": "WEDSTRIJD STARTEN",
    "menu.easy": "MAKKELIJK",
    "menu.medium": "GEMIDDELD",
    "menu.hard": "MOEILIJK",
    "menu.elite": "ELITE",
    "hud.stamina": "UITHOUDINGSVERMOGEN",
    "end.win": "GEWONNEN!",
    "end.loss": "VERLOREN",
    "end.draw": "GELIJKSPEL",
    "end.rematch": "REVANCHE",
    "event.goal": "⚽ DOELPUNT!",
    "event.halftime": "RUST",
    "event.fulltime": "EINDE",
    "settings.title": "INSTELLINGEN",
    "settings.save": "OPSLAAN",
  },

  pl: {
    "menu.title": "⚽ AMN PIŁKA NOŻNA",
    "menu.practice": "TRENING",
    "menu.startMatch": "ZACZNIJ MECZ",
    "menu.easy": "ŁATWY",
    "menu.medium": "ŚREDNI",
    "menu.hard": "TRUDNY",
    "menu.elite": "ELITA",
    "hud.stamina": "WYTRZYMAŁOŚĆ",
    "end.win": "WYGRANA!",
    "end.loss": "PRZEGRANA",
    "end.draw": "REMIS",
    "end.rematch": "REWANŻ",
    "event.goal": "⚽ GOL!",
    "event.halftime": "PRZERWA",
    "event.fulltime": "KONIEC MECZU",
    "settings.title": "USTAWIENIA",
    "settings.save": "ZAPISZ",
  },

  sv: {
    "menu.title": "⚽ AMN FOTBOLL",
    "menu.practice": "TRÄNING",
    "menu.startMatch": "STARTA MATCH",
    "menu.easy": "LÄTT",
    "menu.medium": "MEDEL",
    "menu.hard": "SVÅR",
    "menu.elite": "ELIT",
    "hud.stamina": "UTHÅLLIGHET",
    "end.win": "VANN!",
    "end.loss": "FÖRLUST",
    "end.draw": "OAVGJORT",
    "end.rematch": "REVANSCH",
    "event.goal": "⚽ MÅL!",
    "event.halftime": "HALVTID",
    "event.fulltime": "SLUTSIGNAL",
    "settings.title": "INSTÄLLNINGAR",
    "settings.save": "SPARA",
  },

  tr: {
    "menu.title": "⚽ AMN FUTBOL",
    "menu.practice": "ANTRENMAN",
    "menu.startMatch": "MAÇA BAŞLA",
    "menu.easy": "KOLAY",
    "menu.medium": "ORTA",
    "menu.hard": "ZOR",
    "menu.elite": "ELİT",
    "hud.stamina": "DAYANIKLILIK",
    "end.win": "KAZANDIN!",
    "end.loss": "KAYBETTİN",
    "end.draw": "BERABERE",
    "end.rematch": "RÖVANŞ",
    "event.goal": "⚽ GOL!",
    "event.halftime": "DEVRE ARASI",
    "event.fulltime": "MAÇ SONU",
    "settings.title": "AYARLAR",
    "settings.save": "KAYDET",
  },
};

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "pt", name: "Português" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "zh", name: "中文" },
  { code: "ru", name: "Русский" },
  { code: "ar", name: "العربية" },
  { code: "hi", name: "हिन्दी" },
  { code: "nl", name: "Nederlands" },
  { code: "pl", name: "Polski" },
  { code: "sv", name: "Svenska" },
  { code: "tr", name: "Türkçe" },
];

export class LocalizationSystem {
  private static _lang = "en";

  static setLanguage(code: string): void {
    if (TRANSLATIONS[code]) {
      this._lang = code;
    } else {
      console.warn(`LocalizationSystem: Language '${code}' not found, falling back to 'en'`);
      this._lang = "en";
    }
  }

  static getLanguage(): string {
    return this._lang;
  }

  /**
   * Translate a key. Falls back to English if not found in current language.
   * Falls back to the key itself if not in English either.
   */
  static t(key: string): string {
    const lang = TRANSLATIONS[this._lang];
    if (lang && lang[key] !== undefined) return lang[key];
    // Fallback to English
    const en = TRANSLATIONS["en"];
    if (en && en[key] !== undefined) return en[key];
    // Fallback to key
    return key;
  }
}
